import { useState, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { apiErrorMessage } from "@/lib/http";
import { maskCnpj, maskCep, maskPhone, isValidCnpj, isValidCep, isValidPhone } from "@/lib/format/masks";
import { lookupCep } from "@/lib/format/cep";
import { BR_UFS } from "@/lib/format/uf";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { completeAgencyProfile } from "../api";
import { OnboardingShell } from "../components/OnboardingShell";

interface FormState {
  name: string;
  legalName: string;
  cnpj: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  uf: string;
  instagram: string;
  website: string;
}

const EMPTY: FormState = {
  name: "",
  legalName: "",
  cnpj: "",
  email: "",
  phone: "",
  cep: "",
  street: "",
  number: "",
  neighborhood: "",
  city: "",
  uf: "",
  instagram: "",
  website: "",
};

const inputCls =
  "w-full rounded-xl border border-border bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10";

function validate(f: FormState): Partial<Record<keyof FormState, string>> {
  const e: Partial<Record<keyof FormState, string>> = {};
  if (f.name.trim().length < 2) e.name = "Informe o nome fantasia.";
  if (f.legalName.trim().length < 2) e.legalName = "Informe a razão social.";
  if (!isValidCnpj(f.cnpj)) e.cnpj = "CNPJ inválido.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "E-mail inválido.";
  if (!isValidPhone(f.phone)) e.phone = "Telefone inválido.";
  if (!isValidCep(f.cep)) e.cep = "CEP inválido.";
  if (!f.street.trim()) e.street = "Informe o logradouro.";
  if (!f.number.trim()) e.number = "Informe o número.";
  if (!f.neighborhood.trim()) e.neighborhood = "Informe o bairro.";
  if (!f.city.trim()) e.city = "Informe a cidade.";
  if (!(BR_UFS as readonly string[]).includes(f.uf)) e.uf = "UF inválida.";
  return e;
}

export function AgencyProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);

  const [form, setForm] = useState<FormState>(() => ({ ...EMPTY, email: "" }));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  // Só se chega aqui autenticado (via login social / cadastro).
  if (!user) return <Navigate to="/login" replace />;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleCepBlur() {
    if (!isValidCep(form.cep)) return;
    setCepLoading(true);
    try {
      const addr = await lookupCep(form.cep);
      setForm((prev) => ({
        ...prev,
        street: addr.street || prev.street,
        neighborhood: addr.neighborhood || prev.neighborhood,
        city: addr.city || prev.city,
        uf: addr.state || prev.uf,
      }));
    } catch {
      toast.error("Não foi possível buscar o CEP. Preencha o endereço manualmente.");
    } finally {
      setCepLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSubmitting(true);
    try {
      await completeAgencyProfile({
        name: form.name.trim(),
        legalName: form.legalName.trim(),
        cnpj: form.cnpj,
        email: form.email.trim(),
        phone: form.phone,
        address: {
          street: form.street.trim(),
          number: form.number.trim(),
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.uf,
          zipCode: form.cep,
        },
        social: {
          instagram: form.instagram.trim() || undefined,
          website: form.website.trim() || undefined,
        },
      });
      patchUser({ profileCompleted: true });
      navigate("/onboarding/terms", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Não foi possível salvar os dados. Tente novamente."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OnboardingShell activeStep={1}>
      <Card className="p-7 shadow-card border-border/60">
        <div className="mb-6">
          <h1 className="text-xl font-bold tracking-tight text-ink">Cadastre sua imobiliária</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Esses dados identificam sua organização no ImobGo e ficam disponíveis em Configurações.
            Você poderá vincular corretores depois.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <SectionTitle>Identificação</SectionTitle>

          <Field label="Nome fantasia" error={errors.name}>
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Horizonte Imóveis"
              autoComplete="organization"
            />
          </Field>

          <Field label="Razão social" error={errors.legalName}>
            <input
              className={inputCls}
              value={form.legalName}
              onChange={(e) => set("legalName", e.target.value)}
              placeholder="Horizonte Gestão Imobiliária LTDA"
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="CNPJ" error={errors.cnpj}>
              <input
                className={inputCls}
                value={form.cnpj}
                onChange={(e) => set("cnpj", maskCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
              />
            </Field>
            <Field label="Telefone" error={errors.phone}>
              <input
                className={inputCls}
                value={form.phone}
                onChange={(e) => set("phone", maskPhone(e.target.value))}
                placeholder="(11) 99999-9999"
                inputMode="tel"
              />
            </Field>
          </div>

          <Field label="E-mail comercial" error={errors.email}>
            <input
              className={inputCls}
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="contato@suaimobiliaria.com.br"
              autoComplete="email"
            />
          </Field>

          <SectionTitle>Endereço</SectionTitle>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr]">
            <Field label="CEP" error={errors.cep} hint={cepLoading ? "buscando…" : undefined}>
              <input
                className={inputCls}
                value={form.cep}
                onChange={(e) => set("cep", maskCep(e.target.value))}
                onBlur={handleCepBlur}
                placeholder="00000-000"
                inputMode="numeric"
              />
            </Field>
            <Field label="Logradouro" error={errors.street}>
              <input
                className={inputCls}
                value={form.street}
                onChange={(e) => set("street", e.target.value)}
                placeholder="Av. Paulista"
                autoComplete="address-line1"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr]">
            <Field label="Número" error={errors.number}>
              <input
                className={inputCls}
                value={form.number}
                onChange={(e) => set("number", e.target.value)}
                placeholder="1000"
              />
            </Field>
            <Field label="Bairro" error={errors.neighborhood}>
              <input
                className={inputCls}
                value={form.neighborhood}
                onChange={(e) => set("neighborhood", e.target.value)}
                placeholder="Bela Vista"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
            <Field label="Cidade" error={errors.city}>
              <input
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="São Paulo"
              />
            </Field>
            <Field label="UF" error={errors.uf}>
              <select className={inputCls} value={form.uf} onChange={(e) => set("uf", e.target.value)}>
                <option value="">—</option>
                {BR_UFS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <SectionTitle>Redes sociais (opcional)</SectionTitle>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Instagram">
              <input
                className={inputCls}
                value={form.instagram}
                onChange={(e) => set("instagram", e.target.value)}
                placeholder="@suaimobiliaria"
              />
            </Field>
            <Field label="Site">
              <input
                className={inputCls}
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="www.suaimobiliaria.com.br"
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-grad py-3.5 text-sm font-bold text-white shadow-glow transition-all hover:scale-[1.01] hover:shadow-argon-hover active:scale-[0.99] disabled:opacity-70",
              submitting && "cursor-not-allowed opacity-70",
            )}
          >
            {submitting ? <IconLoader2 className="animate-spin" size={18} /> : "Continuar"}
          </button>
        </form>
      </Card>
    </OnboardingShell>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-soft">
      {children}
    </p>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-ink-muted">{label}</label>
        {hint && <span className="text-[11px] text-ink-soft">{hint}</span>}
      </div>
      {children}
      {error && (
        <p role="alert" className="text-[12px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
